import { Database, Image, Mail } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { companyRepository } from '../../services/companyRepository'
import { CreateCompanyForm } from '../forms/CreateCompanyForm'
import { AppHeader } from '../layout/AppHeader'

import { CompaniesTable } from './CompaniesTable'
import { DeleteCompanyDialog } from './DeleteCompanyDialog'
import { EditCompanyDialog } from './EditCompanyDialog'

import type { Company } from '../../types/company'
import type { CompanyMutationInput, EditCompanyMutationInput } from '../../types/forms'

export function CompaniesDashboard() {
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [deletingCompany, setDeletingCompany] = useState<Company | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadCompanies = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setCompanies(await companyRepository.list())
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'No fue posible cargar los datos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadCompanies()
  }, [loadCompanies])

  const createCompany = useCallback(async (input: CompanyMutationInput) => {
    const { company } = await companyRepository.create(input)
    setCompanies((current) => [company, ...current])
    toast.success('Empresa guardada', {
      description: `${company.name} ya aparece en el directorio.`,
    })
  }, [])

  const updateCompany = useCallback(async (companyId: string, input: EditCompanyMutationInput) => {
    const result = await companyRepository.update(companyId, input)
    setCompanies((current) =>
      current.map((company) => (company.id === companyId ? result.company : company)),
    )
    setEditingCompany(null)
    if (result.warning) {
      toast.warning('Cambios guardados con una advertencia', {
        description: result.warning,
        duration: 9000,
      })
    } else {
      toast.success('Cambios guardados', {
        description: `${result.company.name} se actualizó correctamente.`,
      })
    }
  }, [])

  const deleteCompany = useCallback(async (company: Company) => {
    setDeleting(true)
    try {
      const result = await companyRepository.delete(company.id)
      setCompanies((current) => current.filter(({ id }) => id !== company.id))
      setDeletingCompany(null)
      if (result.warning) {
        toast.warning('Empresa eliminada con una advertencia', {
          description: result.warning,
          duration: 12_000,
        })
      } else {
        toast.success('Empresa eliminada', {
          description: `${company.name} ya no está en el directorio.`,
        })
      }
    } catch (deleteError) {
      toast.error('No se pudo eliminar', {
        description: deleteError instanceof Error ? deleteError.message : 'Inténtalo de nuevo.',
      })
    } finally {
      setDeleting(false)
    }
  }, [])

  const emailCount = companies.reduce((total, company) => total + company.emails.length, 0)
  const imageCount = companies.filter(({ image }) => image).length

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="mx-auto w-full max-w-[1500px] px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-moss">
              Panel de administración
            </p>
            <h1 className="mt-2 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              Tu red empresarial, en orden.
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-ink/60">
              Registra empresas, centraliza sus contactos y mantén sus datos siempre disponibles
              para el equipo.
            </p>
          </div>
          <dl className="grid grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: 'Empresas', value: companies.length, icon: Database },
              { label: 'Correos', value: emailCount, icon: Mail },
              { label: 'Imágenes', value: imageCount, icon: Image },
            ].map(({ label, value, icon: Icon }) => (
              <div
                key={label}
                className="min-w-24 rounded-2xl border border-white/70 bg-white/75 px-4 py-3 shadow-sm"
              >
                <dt className="flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-wide text-ink/45">
                  <Icon aria-hidden="true" className="h-3.5 w-3.5" /> {label}
                </dt>
                <dd className="mt-1 font-display text-2xl font-bold">{loading ? '—' : value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mx-auto max-w-3xl">
          <CreateCompanyForm onCreate={createCompany} />
        </div>

        <div className="mt-8">
          <CompaniesTable
            companies={companies}
            loading={loading}
            error={error}
            onRetry={() => void loadCompanies()}
            onEdit={setEditingCompany}
            onDelete={setDeletingCompany}
          />
        </div>
      </main>

      <EditCompanyDialog
        company={editingCompany}
        open={Boolean(editingCompany)}
        onOpenChange={(open) => !open && setEditingCompany(null)}
        onSave={updateCompany}
      />
      <DeleteCompanyDialog
        company={deletingCompany}
        open={Boolean(deletingCompany)}
        deleting={deleting}
        onOpenChange={(open) => !open && setDeletingCompany(null)}
        onConfirm={deleteCompany}
      />
    </div>
  )
}
